import {
  Table as MaUTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Pagination } from "@material-ui/lab";
import { clsx } from "clsx";
import qs from "query-string";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Cell, Row, usePagination, useSortBy, useTable } from "react-table";
import { scrollStyles } from "../../theme/defaultStyles";
import { setParams } from "../Roles/Admin/AllClients/hooks/useFilter";

const useStyles = makeStyles((theme: Theme) => ({
  rowBody: {},
  table: {
    overflowX: "auto",

    ...scrollStyles({ color: theme.palette.primary.main }),

    "@global": {
      th: {
        position: "sticky",
        top: 0,
        backgroundColor: "transparent",
        zIndex: 1000,
      },
      "th, td": {
        cursor: "default",
        fontSize: ".85rem",
      },
      // "tbody tr:nth-child(even)": {
      //   backgroundColor: "#f2f2f2",
      // },
    },
  },
}));

interface IMigomTable {
  columns: any[];
  data: Record<string, any>[];
  customClasses?: Record<string, any>;
  pageCount?: number;
  onRowClick?: (rowData: Record<string, any>, index: number) => void;
  selectedItems?: any[];
  makeRowClass?: any;
  rowId?: string;
  totalText?: any;
  totalCount?: number;
  hidePagination?: boolean;
  onCell?: (cell: Cell) => void;
  fetchData?: any;
  customSort?: (data?: Record<string, any>) => void;
  autoHeight?: boolean;
  useFeatures?: boolean;
  useScrollable?: boolean;
  hideBottomBlock?: boolean;
  renderChild?: (row: Row, index: number) => any;
  customStyle?: Record<string, any>;
  currentPageIndex?: number;
  setCurrentPageIndex?: (value: number) => void;
  fullWidth?: boolean;
  row?: boolean;
}

const MigomTable = memo(
  ({
    columns,
    data,
    customClasses,
    pageCount: controlledPageCount,
    onRowClick,
    selectedItems,
    makeRowClass,
    totalText,
    totalCount,
    hidePagination,
    onCell,
    fetchData,
    customSort,
    autoHeight,
    useFeatures,
    useScrollable,
    hideBottomBlock,
    renderChild,
    customStyle,
    currentPageIndex,
    setCurrentPageIndex,
  }: IMigomTable) => {
    const classes = useStyles();

    const config: any = {
      columns,
      data,
      initialState: { pageIndex: currentPageIndex || 0 },
      manualPagination: true,
      manualSortBy: true,
      pageCount: controlledPageCount,
    };

    const useControlledState = (state) => {
      return useMemo(
        () => ({
          ...state,
          pageIndex: currentPageIndex,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state, currentPageIndex]
      );
    };

    if ((currentPageIndex as number) >= 0) {
      config.useControlledState = useControlledState;
    }

    const {
      getTableProps,
      headerGroups,
      rows,
      prepareRow,
      pageCount,
      gotoPage,
      state,
    } = useTable(config, useSortBy, usePagination) as any;

    const { pageIndex, sortBy } = state as any;

    const initial = useRef<any>(false);

    useEffect(() => {
      if (useFeatures && initial.current) {
        setParams({
          page: pageIndex + 1,
          sortBy: sortBy?.length ? sortBy?.[0]?.id : undefined,
          sortDirection: sortBy?.length
            ? sortBy?.[0]?.desc
              ? "desc"
              : "asc"
            : undefined,
        });
      }

      if (!useFeatures && fetchData) {
        fetchData({ pageIndex, sortBy });
      }

      initial.current = true;
    }, [fetchData, pageIndex, sortBy, useFeatures]);

    useEffect(() => {
      if (customSort) {
        if (sortBy?.length > 0) {
          customSort({
            sortBy: sortBy?.[0]?.id,
            sortDirection: sortBy?.[0]?.desc ? "desc" : "asc",
          });
        } else {
          customSort();
        }
      }
    }, [customSort, sortBy]);

    const makeHeadRows = headerGroups.map((headerGroup, index) => {
      const makeCells = headerGroup.headers.map((column, indx) => {
        const columnAsAny = column as any;
        const toggleSort = columnAsAny.getSortByToggleProps();

        return (
          <TableCell
            {...columnAsAny.getHeaderProps([
              {
                id: columnAsAny.id,
                className: columnAsAny.classNameHead,
                style: {
                  whiteSpace: "nowrap",
                  fontWeight: 700,
                  borderBottom: 0,
                  padding: "8px 16px",
                  fontSize: ".75rem",
                  letterSpacing: "0.04em",
                  ...(columnAsAny.styleHead || {}),
                  cursor: toggleSort?.style?.cursor || "default",
                  zIndex: 0,
                },
                onClick: toggleSort?.onClick,
              },
            ])}
            key={indx}
          >
            <>
              {columnAsAny.render("Header")}
              <span>
                {columnAsAny.isSorted
                  ? columnAsAny.isSortedDesc
                    ? " 🔽"
                    : " 🔼"
                  : ""}
              </span>
            </>
          </TableCell>
        );
      });

      return (
        <TableRow {...headerGroup.getHeaderGroupProps()} key={index}>
          {makeCells}
        </TableRow>
      );
    });

    const makeBodyRows = rows.map((row, i) => {
      prepareRow(row);

      const makeCells = row.cells.map((cell, indx) => {
        let onClick: any;

        if ((cell.column as any)?.onCell) {
          onClick = (cell.column as any).onCell;
        }

        if (onCell) {
          onClick = () => {
            onCell(cell);
          };
        }

        const column = cell.column as any;

        return (
          <TableCell
            {...cell.getCellProps([
              {
                className: column.className,
                style: {
                  padding: "8px 16px",
                  /* borderBottom: 0, */
                  ...(column.style || {}),
                  cursor: onRowClick || onCell ? "pointer" : "default",
                  color: "inherit",
                },
              },
            ])}
            key={indx}
            onClick={onClick}
          >
            {cell.render("Cell")}
          </TableCell>
        );
      });

      const findItem = (selectedItems || []).find((item) => {
        return item.id === row.original?.id;
      });

      return (
        <React.Fragment key={i}>
          <TableRow
            {...(row as any).getRowProps([
              {
                className: clsx(
                  classes.rowBody,
                  makeRowClass ? makeRowClass(row.original, i) : null,
                  Boolean(findItem) && customClasses?.rowActive
                ),
                onClick: onRowClick
                  ? () => onRowClick(row.original, i)
                  : undefined,
                style: { cursor: onRowClick ? "pointer" : "default" },
              },
            ])}
          >
            {makeCells}
          </TableRow>

          {renderChild && renderChild(row, i)}
        </React.Fragment>
      );
    });

    const onChangePagination = (e, page) => {
      const result = page - 1;

      if (useFeatures) {
        setParams({ page: page });
      } else if (setCurrentPageIndex) {
        setCurrentPageIndex(result);
      } else {
        gotoPage(result);
      }
    };

    const [style, setStyle] = useState({});

    const onResize = useCallback(() => {
      const elements = document.querySelectorAll(`.${classes.table}`);
      if (!autoHeight && elements?.length === 1) {
        setStyle((prevState) => ({
          ...prevState,
          height:
            window.innerHeight - elements[0]?.getBoundingClientRect().top - 100,
        }));
      }
    }, [autoHeight, classes.table]);

    useEffect(() => {
      onResize();
    }, [onResize]);

    useEffect(() => {
      window.addEventListener("resize", onResize, false);

      return () => {
        window.removeEventListener("resize", onResize, false);
      };
    }, [onResize]);

    const params: Record<string, any> = qs.parse(window.location.search);

    return (
      <>
        <div
          className={classes.table}
          style={
            autoHeight
              ? { ...customStyle }
              : {
                  ...(useScrollable ? style : {}),
                  minHeight: 300,
                  ...customStyle,
                }
          }
        >
          <MaUTable {...getTableProps()}>
            <TableHead>{makeHeadRows}</TableHead>
            <TableBody>{makeBodyRows}</TableBody>
          </MaUTable>
        </div>

        <div className="h-2.5" />

        {!hideBottomBlock && (
          <>
            {!hidePagination && (
              <div className="flex flex-1 justify-center">
                <Pagination
                  count={controlledPageCount || pageCount}
                  page={params.page >= 1 ? +params.page : pageIndex + 1}
                  onChange={onChangePagination}
                  size="large"
                />
              </div>
            )}

            {totalText && (
              <div className="flex justify-center text-base flex-1 items-end">
                <span className="mr-2">{totalText}:</span>
                <b>{totalCount || 0}</b>
              </div>
            )}
          </>
        )}
      </>
    );
  }
);

export default MigomTable;
